<script lang="ts" setup>
    import Slider from '@shared/components/inputs/Slider.vue';
    import { ColorFormatResult, formatColor, hslResult } from '@shared/utils/parsers';
    import { computed, onMounted, reactive, ref } from 'vue';
    import { useColorManager } from './useColorManager';
    import { useSavedColors } from './useSavedColors';

    const props = withDefaults(
        defineProps<{
            colors: { color: string | HSLArray; name: string }[];
            position?: string;
        }>(),
        {
            position: 'bottom center',
        }
    );

    const emit = defineEmits<{
        (ev: 'update:color', value: { color: string; name: string }): void;
    }>();

    const selectedColorIndex = ref(0);
    const selectedColor = computed(() => props.colors[selectedColorIndex.value]);
    const colorInput = ref('');

    const { addCurrentColorToSaved, applySavedColor, savedColors } = useSavedColors();

    const parsedColor = reactive(
            typeof props.colors[0].color === 'string'
                ? (formatColor(props.colors[0].color, 'hsl') as ColorFormatResult<HSLArray>)
                : hslResult(props.colors[0].color)
        ),
        computedPosition = computed(() => {
            let [p1, p2] = props.position.split(' '),
                res: { [k: string]: string } = {};

            if (p1 === 'center') res.top = '50%';
            else if (p1 === 'bottom') res.top = '100%';
            else if (p1 === 'top') res.bottom = '100%';

            if ((p2 || p1) === 'center') res.left = '50%';
            else if ((p2 || p1) === 'left') res.right = '100%';
            else if ((p2 || p1) === 'right') res.left = '100%';

            res.transform = `translate3d(${/left|right/.test(p2 || p1) ? 0 : 'calc(-50% - var(--pos-margin)/2)'}, ${/top|bottom/.test(p1) ? 0 : 'calc(-50% - var(--pos-margin)/2)'}, 0)`;

            return res;
        });
    const { applyColorFromInput, getValuesFromPoint, gradientRef, switchColor, clr, state, updatePointerPosition } = useColorManager(parsedColor);

    const submitColor = () => {
        const emittedColor = {
            color: `hsla(${clr.hue}, ${clr.saturation}%, ${clr.lightness}%, ${clr.alpha / 100})`,
            name: selectedColor.value.name,
        };
        emit('update:color', emittedColor);
    };

    const handleSwitchColor = (index: number) => {
        switchColor(index, selectedColorIndex, props.colors);
    };

    onMounted(() => {
        console.log('props.colors', props.colors);

        const newColor = props.colors[selectedColorIndex.value]?.color ?? props.colors[0].color;
        const result = typeof newColor === 'string' ? (formatColor(newColor, 'hsl') as ColorFormatResult<HSLArray>) : hslResult(newColor);

        clr.hue = result.values[0];
        clr.saturation = result.values[1];
        clr.lightness = result.values[2];
        clr.alpha = result.values[3] ?? 100;

        parsedColor.values = result.values;
        parsedColor.string = result.string;

        updatePointerPosition();
    });
</script>

<template>
    <div class="cp-container" :style="computedPosition">
        <div class="wrapper">
            <div class="button-wrapper p-s-lr">
                <button
                    v-for="(color, index) in props.colors"
                    :key="color.name"
                    class="btn btn-sm btn-block m-xs-t"
                    :class="{ 'active btn-primary': index === selectedColorIndex }"
                    @click="handleSwitchColor(index)">
                    {{ color.name }}
                </button>
            </div>

            <div class="input-wrapper">
                <input v-model="colorInput" placeholder="Enter HSL or RGB" class="color-input" />
                <button class="btn btn-primary btn-sm" @click="() => applyColorFromInput(colorInput)">Apply Input Color</button>
            </div>

            <div class="fl" :style="`--cp-hue: ${clr.hue}`">
                <div class="cp-colors-container" ref="gradientRef" @pointerdown.left.prevent.stop="getValuesFromPoint">
                    <span
                        class="color-pick"
                        :style="`--active-color: ${parsedColor.string};
                  --pos-x: ${state.x}px;
                  --pos-y: ${state.y}px`" />
                </div>

                <Slider info="hidden" class="color-picker hue" :max="360" direction="vertical" v-model:value="clr.hue" />
                <Slider info="hidden" class="color-picker alpha" direction="vertical" v-model:value="clr.alpha" />
            </div>

            <!-- Actions -->
            <div class="button-wrapper">
                <button class="btn btn-primary btn-sm btn-block m-xs-t" @click="submitColor">Apply Color</button>
                <button class="btn btn-primary btn-sm btn-block m-xs-t" @click="() => addCurrentColorToSaved(clr)">Save Color</button>
            </div>

            <!-- Saved Colors -->
            <div class="saved-colors">
                <div
                    class="saved-color"
                    v-for="color in savedColors"
                    :key="color"
                    :style="{ backgroundColor: color }"
                    @click="applySavedColor(color, clr)"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        position: relative;
    }
    .p-s-lr {
        padding: 0 20px;
    }
    .button-wrapper {
        display: flex;
        justify-content: center;
        gap: 1rem;
    }
    .saved-colors {
        margin-top: 1rem;
        display: flex;
        flex-wrap: wrap;
        overflow: auto;
    }
    .saved-color {
        width: 20px;
        height: 20px;
        margin: 2px;
        border: 1px solid #000;
        cursor: pointer;
    }
</style>

<!-- **** --------------------------------------------------------------------------
	STYLES
-------------------------------------------------------------------------- **** -->
<style>
    /* hue rail bg */
    .ugs-slider-container.color-picker.hue .ugs-slider-rail {
        background-image: linear-gradient(to bottom, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%);
    }

    /* alpha rail bg */
    .ugs-slider-container.color-picker.alpha .ugs-slider-rail {
        background-color: #fff;
        background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(135deg, #ccc 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(135deg, transparent 75%, #ccc 75%);
        background-size: 8px 8px;
        background-position:
            0 0,
            4px 0,
            4px -4px,
            0px 4px;

        border-radius: 0 1px 1px 0;
    }

    .ugs-slider-container.color-picker {
        height: 160px;
    }
    .active-btn {
        font-weight: bold;
    }

    .color-pick {
        --active-color: hsl(0, 0%, 0%, 100%);
        --pick-size: 12px;
        --pos-x: 0px;
        --pos-y: 0px;

        display: block;
        position: absolute;
        height: var(--pick-size);
        width: var(--pick-size);
        top: 0;
        left: 0;

        transform: translate3d(calc(var(--pos-x) - 50%), calc(var(--pos-y) - 50%), 0);

        border-radius: 50%;
        outline: 2px solid white;
        border: 1px solid rgb(40, 36, 43);

        background-color: var(--active-color);

        pointer-events: none;
    }

    .cp-colors-container {
        display: flex;
        position: relative;
        flex-direction: column;
        justify-content: center;
        height: 10rem;
        width: 10rem;

        /* outline: 1px solid #c9a4c8; */

        border-radius: 1px 0 0 1px;

        background-image: linear-gradient(to bottom, transparent 0%, #000 100%), linear-gradient(to right, #fff 0%, transparent 100%);
        background-color: hsl(var(--cp-hue), 100%, 50%);

        overflow: hidden;
    }

    .cp-color-swatch,
    .cp-color {
        display: block;
        height: 100%;
        width: 100%;
    }
    .cp-color {
        transform: scale(1.5);
        pointer-events: none;
    }

    .cp-container {
        --pos-margin: 0.6rem;

        display: flex;
        flex-direction: column;
        position: absolute;
        padding: 0.4rem 0.4rem 0.8rem;
        margin: var(--pos-margin);

        background-color: #fff;
        border-radius: 2px;

        box-shadow:
            0.1rem 0.5rem 0.7rem -0.2rem #00000015,
            -0.4rem -0.3rem 1rem -0.6rem #00000040;
        z-index: 4;
    }
</style>
